import React, { useState } from 'react';
import { COLORS, AI_ICON_B64, BUREAUS, sharedStyles, BureauCard, callClaude } from '../shared';

const PROCESS_STEPS = [
  { num:1, label:'Report Received'    },
  { num:2, label:'Formatting Data'    },
  { num:3, label:'Verifying Info'     },
  { num:4, label:'Ready for Strategy' },
];

export default function ReportFormatter({ onComplete }) {
  const [pasteText, setPasteText]       = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [error, setError]               = useState(null);

  const simulateSteps = async () => {
    for (let i = 1; i <= 4; i++) {
      setProcessingStep(i);
      await new Promise(r => setTimeout(r, 750));
    }
  };

  const handleFormat = async () => {
    if (!pasteText.trim()) return;
    setIsProcessing(true); setError(null);
    simulateSteps();
    try {
      const prompt = `You are a credit report formatting specialist for Coleman Financial Firm. Extract and organize this credit report into clean structured JSON.
Return ONLY a JSON object with NO markdown or backticks:
{
  "clientName":"","reportDate":"","bureaus":[],
  "scores":{"TransUnion":null,"Experian":null,"Equifax":null},
  "personalInfo":{"name":"","address":"","dob":"","ssn":"XXX-XX-XXXX","employer":""},
  "accounts":[{"creditor":"","accountNumber":"XXXX","type":"","status":"","balance":"","limit":"","paymentHistory":"","bureaus":[],"flags":[]}],
  "inquiries":[{"creditor":"","date":"","bureau":"","type":"hard/soft"}],
  "publicRecords":[{"type":"","date":"","amount":"","status":""}],
  "summary":{"totalAccounts":0,"openAccounts":0,"closedAccounts":0,"negativeItems":0,"totalDebt":"","utilizationRate":"","oldestAccount":"","keyFlags":[]}
}

Credit report text:
${pasteText}`;

      const text   = await callClaude([{ role:'user', content:prompt }]);
      const clean  = text.replace(/```json|```/g,'').trim();
      const parsed = JSON.parse(clean);
      onComplete(parsed);
    } catch(err) {
      setError('Error: ' + (err.message || 'Unable to format report. Please check your text and try again.'));
    } finally {
      setIsProcessing(false); setProcessingStep(0);
    }
  };

  const s = {
    grid:       { display:'grid', gridTemplateColumns:'1fr 360px', gap:20 },
    orDivider:  { textAlign:'center', color: COLORS.gold, fontSize:'11px', letterSpacing:'0.15em', margin:'14px 0' },
    textarea:   { width:'100%', minHeight:280, background:'#0f0f0f', border:'1px solid #2a2a2a', borderRadius:7, color:'#ccc', padding:16, fontSize:'12px', resize:'vertical', outline:'none', boxSizing:'border-box', lineHeight:1.7 },
    rightCol:   { display:'flex', flexDirection:'column', gap:16 },
    stepItem:   { display:'flex', gap:14, alignItems:'flex-start', marginBottom:14 },
    stepIcon:   { width:38, height:38, borderRadius:'50%', background:'#1a1a1a', border:'1px solid #2a2a2a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 },
    stepTitle:  { color: COLORS.gold, fontWeight:600, fontSize:'13px', marginBottom:3 },
    stepDesc:   { color:'#666', fontSize:'12px', lineHeight:1.5 },
    bureauGrid: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginTop:10 },
    sectionLbl: { color: COLORS.gold, fontSize:'10px', letterSpacing:'0.15em', textTransform:'uppercase', fontWeight:600, textAlign:'center', marginBottom:10 },
    privacyBox: { background:'#0f0f0f', border:'1px solid #1e2e1e', borderRadius:8, padding:14 },
    processBar: { background:'#141414', border:`1px solid ${COLORS.border}`, borderRadius:10, padding:'16px 20px', display:'flex', alignItems:'center', gap:16, marginTop:20 },
    stepDot: (a,d) => ({ width:32, height:32, borderRadius:'50%', background: d?COLORS.green:a?COLORS.gold:'#1a1a1a', border:`2px solid ${d?COLORS.green:a?COLORS.gold:'#2a2a2a'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', color:(d||a)?'#000':'#444', fontWeight:700, transition:'all 0.3s' }),
    connector:  { width:20, height:2, background:'#2a2a2a' },
    howToBox:   { background:'rgba(201,168,76,0.06)', border:`1px solid rgba(201,168,76,0.2)`, borderRadius:8, padding:14 },
    howToTitle: { color: COLORS.gold, fontWeight:700, fontSize:'12px', marginBottom:10 },
    howToStep:  { color:'#888', fontSize:'12px', lineHeight:1.7, paddingLeft:4 },
  };

  const canSubmit = pasteText.trim().length > 50;

  return (
    <div style={sharedStyles.main}>
      {/* TOP BAR */}
      <div style={sharedStyles.topBar}>
        <div>
          <div style={sharedStyles.pageTitle}>CREDIT REPORT <span style={sharedStyles.pageTitleAccent}>INTAKE</span></div>
          <div style={sharedStyles.pageSubtitle}>Copy and paste your credit report below. We'll format it for accurate analysis and build your custom dispute strategy.</div>
        </div>
        <div style={sharedStyles.memberBadge}>⭐ CFF MEMBER</div>
      </div>

      <div style={s.grid}>
        {/* LEFT */}
        <div style={sharedStyles.card}>
          <div style={sharedStyles.cardHeader}>
            <div style={sharedStyles.stepBadge}>1</div>
            <div style={sharedStyles.cardTitle}>PASTE YOUR CREDIT REPORT</div>
          </div>
          <div style={{ color:'#666', fontSize:'12px', marginBottom:16 }}>
            Log into SmartCredit, Experian, Equifax, TransUnion, or AnnualCreditReport.com — then copy your full credit report and paste it below.
          </div>

          <textarea
            style={s.textarea}
            placeholder="Paste your full credit report text here...

Tip: In SmartCredit, open your report, select all text (Ctrl+A), copy (Ctrl+C), then paste here (Ctrl+V)."
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
          />

          <div style={{ display:'flex', alignItems:'center', gap:6, color:'#555', fontSize:'11px', marginTop:10 }}>
            🔒 Your data is 100% secure and never stored.
          </div>

          {pasteText.length > 0 && pasteText.length < 50 && (
            <div style={{ color: COLORS.gold, fontSize:'11px', marginTop:8 }}>
              ⚠️ Please paste more of your credit report for accurate results.
            </div>
          )}

          {error && <div style={sharedStyles.errorBox}>⚠️ {error}</div>}

          <button
            style={sharedStyles.formatBtn(!canSubmit || isProcessing)}
            onClick={handleFormat}
            disabled={!canSubmit || isProcessing}
          >
            {isProcessing ? '⏳ FORMATTING YOUR REPORT...' : 'FORMAT MY CREDIT REPORT →'}
          </button>
        </div>

        {/* RIGHT */}
        <div style={s.rightCol}>

          {/* HOW TO COPY */}
          <div style={s.howToBox}>
            <div style={s.howToTitle}>📋 How to Copy Your Report</div>
            {[
              { src:'SmartCredit',          tip:'Log in → View Report → Ctrl+A → Ctrl+C' },
              { src:'Experian',             tip:'Log in → Credit Report → Select All → Copy' },
              { src:'AnnualCreditReport',   tip:'View Report → Highlight all text → Copy' },
            ].map(item => (
              <div key={item.src} style={{ marginBottom:8 }}>
                <div style={{ color:'#ccc', fontSize:'12px', fontWeight:600 }}>{item.src}</div>
                <div style={s.howToStep}>{item.tip}</div>
              </div>
            ))}
          </div>

          {/* WHAT HAPPENS NEXT */}
          <div style={sharedStyles.card}>
            <div style={sharedStyles.cardHeader}>
              <div style={sharedStyles.stepBadge}>2</div>
              <div style={sharedStyles.cardTitle}>WHAT HAPPENS NEXT?</div>
            </div>
            {[
              { icon:'📋', title:'We Format Your Report',  desc:"Our team extracts and organizes your credit data into a clear, standardized format." },
              { icon:'🗄️', title:'Data Verification',      desc:'We verify accuracy and identify key items that impact your score.'                  },
              { icon:'🎯', title:'Ready for Strategy',      desc:'Your formatted report is sent to our Dispute Strategy team for your custom plan.'  },
            ].map(step => (
              <div key={step.title} style={s.stepItem}>
                <div style={s.stepIcon}>{step.icon}</div>
                <div>
                  <div style={s.stepTitle}>{step.title}</div>
                  <div style={s.stepDesc}>{step.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* BUREAUS */}
          <div style={sharedStyles.card}>
            <div style={s.sectionLbl}>We Support Reports From</div>
            <div style={s.bureauGrid}>
              {BUREAUS.map(b => <BureauCard key={b.name} {...b} />)}
            </div>
          </div>

          {/* PRIVACY */}
          <div style={s.privacyBox}>
            <div style={{ display:'flex', alignItems:'center', gap:8, color: COLORS.green, fontSize:'12px', fontWeight:600, marginBottom:6 }}>🛡️ YOUR PRIVACY MATTERS</div>
            <div style={{ color:'#555', fontSize:'11px', lineHeight:1.6 }}>We use bank-level encryption to protect your information. Your report is used only to generate your strategy and is never sold or shared.</div>
          </div>
        </div>
      </div>

      {/* PROCESS BAR */}
      <div style={s.processBar}>
        <img src={`data:image/jpeg;base64,${AI_ICON_B64}`} alt="CFF" style={{ width:44, height:44, borderRadius:8, objectFit:'cover', flexShrink:0 }} />
        <div>
          <div style={{ color: COLORS.gold, fontSize:'12px', fontWeight:600 }}>FORMATTING PROCESS</div>
          <div style={{ color:'#666', fontSize:'11px' }}>Our team is standing by to securely format your credit report for analysis and strategy.</div>
        </div>
        <div style={{ marginLeft:'auto' }}>
          <div style={{ display:'flex', alignItems:'center' }}>
            {PROCESS_STEPS.map((step, i) => (
              <React.Fragment key={step.num}>
                <div style={s.stepDot(processingStep===step.num, processingStep>step.num)}>
                  {processingStep > step.num ? '✓' : step.num}
                </div>
                {i < PROCESS_STEPS.length - 1 && <div style={s.connector} />}
              </React.Fragment>
            ))}
          </div>
          <div style={{ display:'flex', marginTop:4 }}>
            {PROCESS_STEPS.map((step, i) => (
              <div key={step.num} style={{ width: i < PROCESS_STEPS.length-1 ? 52 : 32, fontSize:'9px', color: processingStep>=step.num?COLORS.gold:'#444', textAlign:'center' }}>{step.label}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
